import { formatInTimeZone } from 'date-fns-tz';

/**
 * Returns the ordinal suffix for a given number
 * @param number - The number to get the ordinal suffix for
 * @returns The number with its ordinal suffix (e.g., "1st", "2nd", "3rd", "4th")
 */
export const getOrdinalNum = (number: number): string => {
  let selector: number;

  if ((number > 3 && number < 21) || number % 10 > 3) {
    selector = 0;
  } else {
    selector = number % 10;
  }

  const suffixes = ['th', 'st', 'nd', 'rd'];
  return number + suffixes[selector];
};

/**
 * Formats a date string with options (legacy-friendly)
 * Mirrors the previous formatDate signature used across the app.
 */
export const formatDateDisplay = (
  date: Date,
  includeTime = true,
  includeYear = false,
  useLocalTime = false,
  useOrdinal = true,
  useShortMonth = false
): string => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tz = useLocalTime ? userTimeZone : 'UTC';
  const dayRaw = parseInt(formatInTimeZone(date, tz, 'dd'));
  const startDay = useOrdinal ? getOrdinalNum(dayRaw) : String(dayRaw);

  const startTime = includeTime
    ? ' ' + formatInTimeZone(date, tz, 'HH:mm')
    : '';

  const yearStartStr = includeYear
    ? ' ' + formatInTimeZone(date, tz, 'yyyy')
    : '';
  const month = useShortMonth
    ? formatInTimeZone(date, tz, 'MMM')
    : `of ${formatInTimeZone(date, tz, 'MMMM')}`;
  return `${startTime} ${startDay} ${month}${yearStartStr} `;
};

/** Convenience: format time (local timezone) */
export const formatTime = (date: Date): string => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return formatInTimeZone(date, userTimeZone, 'HH:mm');
};

/** Extract Date from MongoDB ObjectId */
export const getDateFromObjectId = (objectId: string): Date => {
  return new Date(parseInt(objectId.substring(0, 8), 16) * 1000);
};

/**
 * Calculates a payout date skipping weekends and public holidays.
 * Holidays include following fixed MM-DD values (with Monday observance if on Sunday).
 */
export const calculatePayoutDate = (date: Date, daysToAdd: number): Date => {
  const publicHolidays = [
    '01-01',
    '01-02',
    '04-18',
    '04-19',
    '04-21',
    '05-01',
    '06-18',
    '06-19',
    '06-29',
    '08-15',
    '11-01',
    '12-25',
  ];

  const currentDate = new Date(date);

  while (daysToAdd > 0) {
    currentDate.setDate(currentDate.getDate() + 1);

    const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
    const monthDay = currentDate.toISOString().slice(5, 10);
    let isPublicHoliday = publicHolidays.includes(monthDay);

    if (currentDate.getDay() === 1) {
      const previousDay = new Date(currentDate);
      previousDay.setDate(previousDay.getDate() - 1);
      const previousDayMonthDay = previousDay.toISOString().slice(5, 10);
      if (publicHolidays.includes(previousDayMonthDay)) {
        isPublicHoliday = true;
      }
    }

    if (!isWeekend && !isPublicHoliday) {
      daysToAdd--;
    }
  }
  return currentDate;
};

/**
 * Subtracts a number of calendar days from a date string and returns a Date in UTC.
 * @example
 * subtractDaysUTC('2025-01-10T00:00:00Z', 5) // Returns 2025-01-05T00:00:00.000Z
 */
export const subtractDaysUTC = (
  inputDate: string,
  daysToSubtract: number
): Date => {
  const date = new Date(inputDate);
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() - daysToSubtract
    )
  );
};

/**
 * Subtracts days and returns a string like "5 Jan 2025"
 */
export const subtractDaysFromDate = (
  inputDate: string,
  daysToSubtract: number
): string => {
  const date = subtractDaysUTC(inputDate, daysToSubtract);
  return `${date.getUTCDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getUTCMonth()]} ${date.getUTCFullYear()}`;
};

/** Thin wrappers for direct formatting needs */
export const formatDateInTimeZone = (
  date: Date,
  timeZone: string,
  formatString: string
): string => formatInTimeZone(date, timeZone, formatString);
